import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PaymentMethod, PaymentPlanDetailsResponse } from '@frontend/api-payment-plans';
import { convertToCents } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { PaymentMethodItem, usePaymentPlanCardOnFile } from '@frontend/payments-card-on-file';
import { useLocationSurchargeState } from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import {
  Modal,
  MoneyField,
  Text,
  useForm,
  TextLink,
  useModalControl,
  useModalLoadingState,
  EmailField,
} from '@frontend/design-system';
import { useOneTimePayment } from '../../hooks/payment-requests/use-one-time-payment';
import { formatCentsToCurrency } from '../../utils/currency-helpers';
import { EditBillingMethodModal } from './EditBillingMethodModal';

export const OneTimePaymentModal = ({
  planDetails,
  onClose,
}: {
  planDetails: PaymentPlanDetailsResponse;
  onClose: () => void;
}) => {
  const { t } = useTranslation('payments');
  const { setLoading } = useModalLoadingState();
  const locationIds = useMemo(() => [planDetails.locationId], [planDetails.locationId]);
  const { modalProps: editBillingModalProps, triggerProps: editBillingTriggerProps } = useModalControl();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();
  const [payRemainingBalance, setPayRemainingBalance] = useState<boolean>(false);

  const { isAnyEnabled: hasSurchargesEnabled } = useLocationSurchargeState(locationIds);

  const fieldMinimum = useMemo(
    () =>
      planDetails.paymentSummary.remainingAmount < planDetails.schedule.paymentAmount
        ? planDetails.paymentSummary.remainingAmount / 100
        : planDetails.schedule.paymentAmount / 100,
    [planDetails]
  );

  const { getFieldProps, values, seedValues, getErrors } = useForm({
    fields: {
      amount: {
        type: 'money',
        required: true,
        min: fieldMinimum,
        max: planDetails.paymentSummary.remainingAmount / 100,
      },
      email: {
        type: 'email',
        required: true,
        value: planDetails.person.email,
      },
    },
  });

  const amount = convertToCents(Number(values.amount));
  const { editBillingMethodProps, paymentMethodItemProps } = usePaymentPlanCardOnFile({
    locationId: planDetails.locationId,
    paymentAmount: amount,
    patientId: planDetails.person.id,
    patientEmail: planDetails.person.email ?? '',
    setSelectedPM: setSelectedPaymentMethod,
    selectDefaultPaymentMethod: true,
    paymentMethodId: selectedPaymentMethod?.id,
    oneTimePayment: true,
    paymentFrequency: planDetails.schedule.frequency,
  });

  const { makeOneTimePayment } = useOneTimePayment({
    paymentMethod: selectedPaymentMethod,
    planDetails,
    amount,
    email: values.email,
  });

  const handleSubmitPayment = async () => {
    setLoading(true);
    await makeOneTimePayment();
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    if (payRemainingBalance) {
      seedValues({ amount: formatCentsToCurrency(planDetails.paymentSummary.remainingAmount) });
      setPayRemainingBalance(false);
    }
  }, [!!payRemainingBalance]);

  const disableSelectedACHPaymentMethod = selectedPaymentMethod?.type === 'us_bank_account';

  return (
    <>
      <Modal.Header textAlign='left'>{t('One Time Payment')}</Modal.Header>
      <Modal.Body>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
            margin-bottom: ${theme.spacing(2)};
          `}
        >
          <Text weight='bold'>{t('Amount will be applied to the remaining balance as an extra payment.')}</Text>
          <Text size='medium'>{t('Monthly payment will not change. Payment plan will be completed early.')}</Text>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: ${theme.spacing(2)};
            margin-bottom: ${theme.spacing(2)};
            align-items: center;
          `}
        >
          <div>
            <MoneyField
              css={css`
                align-self: start;
              `}
              {...getFieldProps('amount')}
              label={t('Payment Amount')}
              name='amount'
            />
            {hasSurchargesEnabled && (
              <Text size='small' color='subdued'>
                {t('One time payments are subject to surcharging')}
              </Text>
            )}
          </div>
          <TextLink
            onClick={() => setPayRemainingBalance(true)}
            weight='bold'
            css={css`
              margin-top: ${theme.spacing(1)};
            `}
          >
            {t('Pay remaining balance')}
          </TextLink>
        </div>
        <div
          css={css`
            margin-bottom: ${theme.spacing(2)};
            max-width: 100%;
          `}
        >
          <EmailField {...getFieldProps('email')} label={t('Receipt Email')} name='email' />
        </div>
        {selectedPaymentMethod && (
          <PaymentMethodItem
            paymentMethod={selectedPaymentMethod}
            {...paymentMethodItemProps}
            onChange={editBillingTriggerProps.onClick}
            showError
          />
        )}
      </Modal.Body>
      <Modal.Actions
        css={{ button: { width: 'fit-content' }, justifyContent: 'end', marginTop: theme.spacing(2) }}
        onPrimaryClick={handleSubmitPayment}
        primaryLabel={t('Submit')}
        disablePrimary={
          !values.amount || !selectedPaymentMethod?.id || !!getErrors().amount || disableSelectedACHPaymentMethod
        }
        onSecondaryClick={onClose}
        secondaryLabel={t('Cancel')}
      />
      <Modal {...editBillingModalProps} minWidth={600} maxWidth={600}>
        <EditBillingMethodModal
          onClose={editBillingModalProps.onClose}
          handleSetBillingMethod={(pm) => {
            setSelectedPaymentMethod(pm);
            editBillingModalProps.onClose();
          }}
          {...editBillingMethodProps}
        />
      </Modal>
    </>
  );
};
